import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@shared/services/api";
import { ADMIN_PRODUCT_KEYS } from "@shared/constants/queryKeys";
import type { CreateProductData, UpdateProductData, Product } from "@shared/types/product";

interface ApiResponse<T> {
  data: T;
}

export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateProductData): Promise<Product> => {
      const response = (await apiClient.post("/product", data)) as ApiResponse<Product>;
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_PRODUCT_KEYS.all });
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateProductData & { id: string }): Promise<Product> => {
      const { id, ...updateData } = data as any;
      const response = (await apiClient.put(`/product/${id}`, updateData)) as ApiResponse<Product>;
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_PRODUCT_KEYS.all });
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/product/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_PRODUCT_KEYS.all });
    },
  });
}

export function useToggleProductStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { id: string; currentActive: boolean }): Promise<Product> => {
      const { id, currentActive } = params;
      const endpoint = currentActive ? `/product/${id}/deactivate` : `/product/${id}/activate`;
      const response = (await apiClient.patch(endpoint)) as ApiResponse<Product>;
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_PRODUCT_KEYS.all });
    },
  });
}
